import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, List, TrendingUp } from 'lucide-react';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import EventList from '@/components/events/EventList';
import EventCalendar from '@/components/events/EventCalendar';
import EventCreateDialog from '@/components/events/EventCreateDialog';
import EventDetailDialog from '@/components/events/EventDetailDialog';
import { useEvents, useEventStats } from '@/hooks/useEvents';

export default function Events() {
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [filters, setFilters] = useState({
    status: 'pending',
    event_type: null,
    limit: 50,
    offset: 0
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const { data: events = [], isLoading } = useEvents(filters);
  const { data: stats } = useEventStats();

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleEventClick = (eventId) => {
    setSelectedEventId(eventId);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 border-b">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold">Events</h1>
          <p className="hidden sm:block text-sm text-muted-foreground mt-1">
            Track important dates, deadlines, and appointments
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="shrink-0">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Create Event</span>
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex flex-wrap items-center gap-3 p-4 sm:p-6 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Total: {stats.total}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200">
              Pending: {stats.pending}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200">
              Completed: {stats.completed}
            </Badge>
          </div>
          {stats.upcoming && (
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-orange-600" />
              <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                {stats.upcoming} upcoming
              </span>
            </div>
          )}
        </div>
      )}

      {/* Filters & View Toggle */}
      <div className="flex items-center justify-between p-4 border-b bg-muted/50">
        <div className="flex items-center gap-3">
          {/* Status Filter */}
          <select
            value={filters.status || 'all'}
            onChange={(e) => handleFilterChange('status', e.target.value === 'all' ? null : e.target.value)}
            className="px-3 py-1.5 text-sm rounded-md border bg-background"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Type Filter */}
          <select
            value={filters.event_type || 'all'}
            onChange={(e) => handleFilterChange('event_type', e.target.value === 'all' ? null : e.target.value)}
            className="px-3 py-1.5 text-sm rounded-md border bg-background"
          >
            <option value="all">All Types</option>
            <option value="deadline">Deadline</option>
            <option value="appointment">Appointment</option>
            <option value="sale_end">Sale End</option>
            <option value="reminder">Reminder</option>
          </select>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2 p-1 bg-muted rounded-lg">
          <Button
            variant={view === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('list')}
            className="h-8"
          >
            <List className="h-4 w-4 mr-1" />
            List
          </Button>
          <Button
            variant={view === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('calendar')}
            className="h-8"
          >
            <Calendar className="h-4 w-4 mr-1" />
            Calendar
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <LoadingSpinner />
          </div>
        ) : view === 'list' ? (
          <EventList
            events={events}
            onEventClick={handleEventClick}
          />
        ) : (
          <EventCalendar
            events={events}
            onEventClick={handleEventClick}
          />
        )}
      </div>

      {/* Dialogs */}
      {isCreateDialogOpen && (
        <EventCreateDialog
          onClose={() => setIsCreateDialogOpen(false)}
        />
      )}

      {selectedEventId && (
        <EventDetailDialog
          eventId={selectedEventId}
          onClose={() => setSelectedEventId(null)}
          onEmailClick={(emailId) => navigate(`/emails?id=${emailId}`)}
        />
      )}
    </div>
  );
}
